import React, { useEffect } from "react";
import "card-pattern/dist/main.css";
import { TRLandingCards, TRLinks } from "../../../config/tuitionLandingCards";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchProfile, fetchPolicy } from "../../../store/slices/profileSlice";
import Loader from "../../../components/common/Loader";
import Notifications from "../../../components/common/Notifications";
import { trackButtonClick, trackCustomPageView } from '../../../services/analytics';

const TuitionLandingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector((s) => s.profile.profile);
  const profileLoading = useAppSelector((s) => s.profile.loading);
  const profileError = useAppSelector((s) => s.profile.profileError);
  const policyError = useAppSelector((s) => s.profile.policyError);
  const policyUrl = useAppSelector((s) => s.profile?.policyUrl); // Access nested policyUrl

  useEffect(() => {
    if (!profile && !profileLoading && !profileError) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profile, profileLoading, profileError]);

  useEffect(() => {
    if (!policyUrl && !policyError) {
      dispatch(fetchPolicy());
    }
  }, [dispatch, policyError, policyUrl]);

  const updatedCards = TRLandingCards.map((card) => {
    if (card.title === "Tuition reimbursement policy") {
      return {
        ...card,
        link: policyUrl || card.link, 
      };
    }
    return card;
  });

  if (profileLoading) return <Loader />;

  if (profileError) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Notifications messageInfo={profileError} status={false} />
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] lg:!mx-[160px] mx-5!">
      {policyError && !policyError.includes("Policy URL not found") && (
        <div className="mt-4!">
          <Notifications
            messageInfo={policyError}
            status={false}
          />
        </div>
      )}
      <div className="padding-bottom-2u margin-top-4u desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-3.5!">
        <h1 className="greeting">Tuition reimbursement</h1>
        <p className="p3 -book mt-1 desktop:mb-[42px]!">
          Apply for tuition reimbursement, manage your existing application or
          lookup resources here.
        </p>

        <h2 className="h2 tablet:mt-8! mobile:mt-3.5!">
          What would you like to do here?
        </h2>
        <p className="p3 -book mt-2 mb-8">
          Please select one of the following below.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4! desktop:gap-y-8! mt-[32px]!">
          {updatedCards.filter((card) => {
                        // show Manager portal only when the card is marked managerOnly
                        // and the API response indicates the user is a manager
                        if (card.managerOnly) {
                          return Boolean(profile?.isManager);
                        }
                        // Hide policy card if policy URL not found error
                        if (card.title === "Tuition reimbursement policy" && policyError?.includes("Policy URL not found")) {
                          return false;
                        }
                        return true;
                      }).map((card, idx: number) => (
            <fieldset
              key={card.title}
              className="ds-card"
              data-ds-theme="vessel"
              data-ds-variant="thumbnail"
              data-ds-version="2"
              aria-labelledby={`card-${idx}-title`}
            >
              <div className="ds-card__image">
                <img src={card.icon} alt="card icons" width="44" height="44" />
              </div>
              <div className="ds-card__content">
                <div
                  className="p3 -medium mb-2! text-[16px]!"
                  id={`card-${idx}-title`}
                >
                  {card.title}
                </div>
                <div className="ds-card__link-container mt-4">
                  <div className="ds-card__text-link">
                    {typeof card.link === 'string' && card.link.startsWith('http') ? (
                      <a 
                        href={card.link} 
                        target={card.target} 
                        rel="noopener noreferrer" 
                        className="inline-link medium no-underline! text-[16px]!"
                        onClick={() => {
                          console.log(`📊 Analytics: Tuition landing card clicked - ${card.title}`);
                          trackButtonClick(card.linkText ?? 'Open', 'tuition-landing-card', { cardTitle: card.title });
                        }}
                      >
                        {card.linkText ?? "Open"}
                      </a>
                    ) : (
                      <Link
                        to={card.link || "*"} 
                        className="inline-link medium no-underline! text-[16px]!"
                        onClick={() => {
                          console.log(`📊 Analytics: Tuition landing card clicked - ${card.title}`);
                          trackButtonClick(card.linkText ?? 'Open', 'tuition-landing-card', { cardTitle: card.title });
                          trackCustomPageView('0');
                        }}
                      >
                        {card.linkText ?? "Open"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </fieldset>
          ))}
      
        </div>
      </div>
          <div
            className="ds-linklist"
            data-ds-theme="vessel"
            data-ds-version="2"
            data-ds-variant="basic"
          >
            <h2 className="h2" id="link-list--0">
              Do it now
            </h2>
            <ul className="ds-linklist__list" aria-labelledby="link-list--0">
              {TRLinks.map((linkItem) => (
                <li key={linkItem.linkUrl}>
                  {linkItem.target === "_blank" ? (
                    <a
                      href={linkItem.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-analytics-type="link"
                      data-analytics-click={linkItem.linkName}
                      className="no-underline! p3 -medium"
                    >
                      {linkItem.linkName}
                    </a>
                  ) : (
                    <Link
                      to={linkItem.linkUrl}
                      data-analytics-type="link"
                      data-analytics-click={linkItem.linkName}
                      className="no-underline! p3 -medium"
                    >
                      {linkItem.linkName}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
      <div className="mb-10!">
        <div className="mb-[-108px]! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>
    </div>
  );
};

export default TuitionLandingPage;
